<script setup>
import { computed, defineEmits } from 'vue';
import { OnClickOutside } from '@vueuse/components';
import { useToggle } from '@vueuse/core';

import Thumbnail from 'dashboard/components/widgets/Thumbnail.vue';
import MultiselectDropdownItems from 'shared/components/ui/MultiselectDropdownItems.vue';

const props = defineProps({
  options: {
    type: Array,
    default: () => [],
  },
  selectedItem: {
    type: Object,
    default: () => ({}),
  },
  hasThumbnail: {
    type: Boolean,
    default: true,
  },
  multiselectorTitle: {
    type: String,
    default: '',
  },
  multiselectorPlaceholder: {
    type: String,
    default: 'Nenhum',
  },
  noSearchResult: {
    type: String,
    default: 'Nenhum resultado encontrado',
  },
  inputPlaceholder: {
    type: String,
    default: 'Pesquisar',
  },
  querySearch: {
    type: Boolean,
    default: false,
  },
  customClass: {
    type: String,
    default: '',
  },
  hideDropdownHeader: {
    type: Boolean,
    default: false,
  },
  hideAvatar: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['select', 'querySearch']);

const [showSearchDropdown, toggleDropdown] = useToggle(false);

const onCloseDropdown = () => toggleDropdown(false);
const onClickSelectItem = value => {
  if (!props.disabled) {
    emit('select', value);
    onCloseDropdown();
  }
};
const onQuerySearch = search => {
  emit('querySearch', search);
};
const handleToggle = () => {
  if (!props.disabled) {
    toggleDropdown();
  }
};

const hasValue = computed(() => {
  if (props.selectedItem && props.selectedItem.id) {
    return true;
  }
  return false;
});
</script>

<template>
  <OnClickOutside @trigger="onCloseDropdown">
    <div
      class="relative w-full mb-2"
      :class="[customClass, { 'opacity-60 cursor-not-allowed': disabled }]"
    >
      <woot-button
        type="button"
        variant="hollow"
        color-scheme="secondary"
        class="w-full appearance-none !border-none outline outline-2 outline-gray-500 dark:outline-slate-600 px-2 hover:outline-slate-500 dark:hover:outline-slate-600 !bg-gray-100 transition-all"
        :class="{ '!cursor-not-allowed': disabled }"
        :disabled="disabled"
        @click="handleToggle"
      >
        <div class="flex gap-1 items-center">
          <template v-if="!selectedItem?.icon && !hideAvatar">
            <Thumbnail
              v-if="hasValue && hasThumbnail"
              :src="selectedItem.thumbnail"
              size="24px"
              :status="selectedItem.availability_status"
              :username="selectedItem.name"
            />
          </template>
          <template v-else-if="selectedItem && !hideAvatar">
            <slot name="icon" :option="selectedItem" />
          </template>

          <div class="flex justify-between w-full min-w-0 items-center">
            <h4
              v-if="!hasValue"
              class="text-ellipsis text-sm text-slate-800 dark:text-slate-100"
            >
              {{ multiselectorPlaceholder }}
            </h4>
            <h4
              v-else
              class="items-center leading-tight overflow-hidden whitespace-nowrap text-ellipsis text-sm text-slate-800 dark:text-slate-100"
              :title="selectedItem.name"
            >
              {{ selectedItem.name }}
            </h4>
            <i
              v-if="showSearchDropdown && !disabled"
              class="icon i-lucide-chevron-up text-slate-600 mr-1"
            />
            <i
              v-else
              class="icon i-lucide-chevron-down text-slate-600 mr-1"
              :class="{ 'opacity-50': disabled }"
            />
          </div>
        </div>
      </woot-button>

      <div
        v-if="!disabled"
        :class="{ 'dropdown-pane--open': showSearchDropdown }"
        class="dropdown-pane"
      >
        <div
          v-if="!hideDropdownHeader"
          class="flex justify-between items-center mb-1"
        >
          <h4
            class="text-sm text-slate-800 dark:text-slate-100 m-0 overflow-hidden whitespace-nowrap text-ellipsis"
          >
            {{ multiselectorTitle }}
          </h4>
          <woot-button
            icon="dismiss"
            size="tiny"
            color-scheme="secondary"
            variant="clear"
            @click="onCloseDropdown"
          />
        </div>
        <MultiselectDropdownItems
          v-if="showSearchDropdown"
          :options="options"
          :selected-items="[selectedItem]"
          :has-thumbnail="hasThumbnail && !hideAvatar"
          :input-placeholder="inputPlaceholder"
          :no-search-result="noSearchResult"
          :query-search="querySearch"
          @query-search="onQuerySearch"
          @select="onClickSelectItem"
        >
          <template #icon="{ option }">
            <slot name="icon" :option="option" />
          </template>
        </MultiselectDropdownItems>
      </div>
    </div>
  </OnClickOutside>
</template>

<style lang="scss" scoped>
.dropdown-pane {
  @apply box-border top-[2.625rem] w-full;
}
</style>
